/** EMP-WS-UI-001 — Overview cards (Profile + Edit › Overview). Non-sensitive data only. */
import React from 'react';
import { Users, Briefcase, CalendarDays, MapPin, Link2, Unlink, Scale, Clock3, UserCircle2 } from 'lucide-react';
import { Button } from '../../../ui/button';
import { formatCalendarDate, formatDateOnly } from '../../../../utils/formatters';

export const SectionCard = ({ icon: Icon, title, subtitle, action, children, testid, className = '', delay = 0 }) => (
  <section className={`lv-card an-rise p-5 ${className}`} style={{ animationDelay: `${delay}ms` }} data-testid={testid}>
    <div className="flex items-start justify-between gap-3 flex-wrap">
      <div className="flex items-center gap-3">
        {Icon && <span className="h-9 w-9 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center shrink-0"><Icon className="h-4.5 w-4.5" /></span>}
        <div><h2 className="crm-section-title">{title}</h2>{subtitle && <p className="crm-section-sub mt-0.5">{subtitle}</p>}</div>
      </div>
      {action}
    </div>
    <div className="mt-4">{children}</div>
  </section>
);

const TILES = [
  { key: 'team', label: 'Team', icon: Users, cls: 'bg-blue-50 text-blue-600', tint: 'from-blue-50/70' },
  { key: 'designation', label: 'Designation', icon: Briefcase, cls: 'bg-emerald-50 text-emerald-600', tint: 'from-emerald-50/70' },
  { key: 'date_of_joining', label: 'Date of Joining', icon: CalendarDays, cls: 'bg-violet-50 text-violet-600', tint: 'from-violet-50/70', fmt: (v) => formatCalendarDate(v, { fallback: '—' }) },
  { key: 'work_location', label: 'Work Location', icon: MapPin, cls: 'bg-orange-50 text-orange-500', tint: 'from-orange-50/70' },
];

export const InfoTiles = ({ employee }) => (
  <div className="grid grid-cols-2 xl:grid-cols-4 gap-3" data-testid="overview-tiles">
    {TILES.map((t, i) => {
      const raw = employee[t.key]; const Icon = t.icon;
      return (
        <div key={t.key} className={`lv-lift an-rise rounded-xl border border-slate-100 bg-gradient-to-br ${t.tint} to-white p-3.5 flex items-center gap-3`} style={{ animationDelay: `${60 + i * 50}ms` }} data-testid={`overview-tile-${t.key}`}>
          <span className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${t.cls}`}><Icon className="h-5 w-5" /></span>
          <div className="min-w-0"><p className="text-[12px] text-slate-500">{t.label}</p><p className="text-[15px] font-bold text-slate-900 truncate capitalize">{t.fmt ? t.fmt(raw) : (raw || '—')}</p></div>
        </div>
      );
    })}
  </div>
);

export const AttendanceCard = ({ att, delay }) => att ? (
  <SectionCard icon={Clock3} title="Attendance Summary" subtitle={`${att.month}/${att.year} · from recorded sessions`} testid="profile-attendance" delay={delay}>
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {[['Present', att.days_present, 'text-emerald-600', 'bg-emerald-50/60'], ['Absent', att.days_absent, 'text-rose-600', 'bg-rose-50/60'], ['On Leave', att.days_on_leave, 'text-amber-600', 'bg-amber-50/60'], ['Total Hours', att.total_hours, 'text-slate-800', 'bg-slate-50']].map(([l, v, c, bg]) => (
        <div key={l} className={`rounded-xl border border-slate-100 ${bg} p-3 text-center`}><p className={`text-[22px] font-extrabold tabular-nums ${c}`}>{v ?? 0}</p><p className="text-[12px] text-slate-500">{l}</p></div>
      ))}
    </div>
  </SectionCard>
) : null;

export const LinkedAccountCard = ({ employee, readOnly, onLink, onUnlink, delay }) => (
  <SectionCard icon={Link2} title="Linked User Account" subtitle="Leave balances and adjustments resolve through this account" testid="profile-linked-account" delay={delay}
    action={!readOnly && (
      <div className="flex gap-2">
        <Button variant="outline" size="sm" className="an-btn h-8 rounded-lg" onClick={onLink} data-testid="profile-link-account-btn"><Link2 className="w-3.5 h-3.5 mr-1" /> {employee.linked_user_id ? 'Change Link' : 'Link Account'}</Button>
        {employee.linked_user_id && <Button variant="outline" size="sm" className="an-btn h-8 rounded-lg text-rose-600 border-rose-200 hover:bg-rose-50" onClick={onUnlink} data-testid="profile-unlink-account-btn"><Unlink className="w-3.5 h-3.5 mr-1" /> Unlink</Button>}
      </div>
    )}>
    <div className="flex items-center gap-3">
      <span className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${employee.linked_user_id ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}><UserCircle2 className="h-5 w-5" /></span>
      <div className="min-w-0">
        {employee.linked_user_id ? (
          <><p className="text-[13.5px] font-semibold text-slate-800 truncate" data-testid="profile-link-status">Linked — {employee.linked_user_email}</p>
            <p className="text-[12px] text-slate-400 truncate">{employee.linked_user_name}{employee.linked_at ? ` · linked ${formatDateOnly(employee.linked_at)}` : ''}</p></>
        ) : (
          <><p className="text-[13.5px] font-semibold text-slate-600" data-testid="profile-link-status">Not linked</p>
            <p className="text-[12px] text-slate-400">No system user account is linked. Leave balances fall back to work-email matching until a link is set.</p></>
        )}
      </div>
    </div>
  </SectionCard>
);

export const LeaveBalancesCard = ({ leaveBal, onHistory, delay }) => (leaveBal && leaveBal.balances?.length > 0) ? (
  <SectionCard icon={Scale} title="Leave Balances" subtitle={`${leaveBal.year} · adjustments live in Leave Management`} testid="profile-leave-balances" delay={delay}
    action={<Button variant="outline" size="sm" className="an-btn h-8 rounded-lg" onClick={onHistory} data-testid="profile-adjustment-history-btn">Adjustment History</Button>}>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {leaveBal.balances.map(b => {
        const pct = b.total_quota > 0 ? Math.min(100, Math.round((b.available / b.total_quota) * 100)) : 0;
        return (
          <div key={b.leave_type_id} className="rounded-xl border border-slate-100 bg-slate-50/60 p-3">
            <p className="text-[12px] font-medium text-slate-500 truncate">{b.leave_type_name}</p>
            <p className="text-[20px] font-extrabold text-slate-900 tabular-nums leading-tight">{b.available}<span className="text-[11px] font-normal text-slate-400"> / {b.total_quota}</span></p>
            <div className="mt-1.5 h-1.5 rounded-full bg-slate-200/70 overflow-hidden"><div className="h-full rounded-full bg-violet-500 ews-bar" style={{ width: `${pct}%` }} /></div>
            <p className="text-[11px] text-slate-400 mt-1">Used {b.used} · Pending {b.pending}{b.manual_adjustment ? ` · Adj ${b.manual_adjustment > 0 ? '+' : ''}${b.manual_adjustment}` : ''}</p>
          </div>
        );
      })}
    </div>
  </SectionCard>
) : null;
